import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { astraAPI } from '../../../services/astraApi';
import { SkeletonList } from '../../../components/ui/Skeleton';

/**
 * AI Decision Feed - ENHANCED (2025-12-30)
 *
 * This is ASTRA's unique differentiator - AI-powered trading decisions.
 * Made more prominent and visually impactful in the optimized layout.
 *
 * Features:
 * - Large confidence indicators that "pop"
 * - Color-coded decision badges
 * - Clear reasoning display
 * - Execution status tracking
 */

const AIDecisionFeed = ({ refreshTrigger = 0 }) => {
  const [selectedConfidence, setSelectedConfidence] = useState('all');
  const [aiDecisions, setAiDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  // 🔧 2025-12-30 FIX: Prevent flickering on refresh - only show skeleton on INITIAL load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null); // Track expanded decision for detailed reasoning

  useEffect(() => {
    fetchAIDecisions();
  }, [refreshTrigger]);

  const fetchAIDecisions = async () => {
    try {
      // 🔧 2025-12-30 FIX: Only show loading skeleton on INITIAL load
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      let decisions = [];

      // Try to get AI signals from API
      try {
        const signals = await astraAPI.getAISignals();
        if (signals && signals.length > 0) {
          decisions = signals.slice(0, 5).map((signal, index) => ({
            id: signal.id || `AI${String(index + 1).padStart(3, '0')}`,
            symbol: signal.symbol,
            decision: (signal.action || signal.side || 'HOLD').toUpperCase(),
            confidence: signal.confidence || 0.5,
            reasoning: signal.reasoning || signal.rationale || `AI analysis for ${signal.symbol}`,
            timestamp: new Date(signal.timestamp || signal.created_at),
            factors: signal.factors || ['AI Analysis'],
            executed: signal.executed || false,
            targetPrice: signal.target_price || 0,
            currentPrice: signal.current_price || 0,
            // Enhanced: Include agent reasoning chain for expandable view
            agentOutputs: signal.agent_outputs || [
              { name: 'Market Scanner', output: signal.market_analysis || 'Analyzed market conditions and price momentum' },
              { name: 'Sentiment Agent', output: signal.sentiment_analysis || 'Evaluated news sentiment and social signals' },
              { name: 'Risk Manager', output: signal.risk_analysis || 'Assessed position risk and portfolio impact' },
              { name: 'Decision Engine', output: signal.decision_rationale || 'Synthesized signals into final recommendation' }
            ],
            riskMetrics: signal.risk_metrics || {
              positionSize: signal.position_size || '2%',
              stopLoss: signal.stop_loss || '-5%',
              takeProfit: signal.take_profit || '+10%',
              riskRewardRatio: signal.risk_reward || '2:1'
            }
          }));
        } else {
          // Fallback: Try to get recent orders with AI confidence
          const orders = await astraAPI.getTradingOrders('all', 5, 1453);
          if (orders && orders.length > 0) {
            decisions = orders.map((order, index) => ({
              id: order.id || `AI${String(index + 1).padStart(3, '0')}`,
              symbol: order.symbol,
              decision: (order.side || 'BUY').toUpperCase(),
              confidence: order.confidence || 0.5,
              reasoning: order.reasoning || `Trade execution for ${order.symbol}`,
              timestamp: new Date(order.timestamp || order.created_at),
              factors: order.factors || ['Automated Trading'],
              executed: order.status === 'completed' || order.status === 'filled',
              targetPrice: order.target_price || order.price,
              currentPrice: order.price || 0,
              // Default agent outputs for orders
              agentOutputs: [
                { name: 'Order Router', output: `Routed ${order.side || 'BUY'} order for ${order.symbol}` },
                { name: 'Execution Engine', output: `Executed at ${order.price || 'market'} price` }
              ],
              riskMetrics: {
                positionSize: '2%',
                stopLoss: '-5%',
                takeProfit: '+10%',
                riskRewardRatio: '2:1'
              }
            }));
          }
        }
      } catch (apiErr) {
        console.error('Failed to fetch AI decisions:', apiErr);
        setError('No AI decision data available');
      }

      setAiDecisions(decisions);
    } catch (err) {
      console.error('Failed to fetch AI decisions:', err);
      setError('Failed to load AI decisions');
      setAiDecisions([]);
    } finally {
      setLoading(false);
      // 🔧 FIX: Mark initial load complete - subsequent refreshes won't show skeleton
      setIsInitialLoad(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString('en-IN');
  };

  const getDecisionColor = (decision) => {
    switch (decision) {
      case 'BUY': return 'text-success bg-success/10 border-success/20';
      case 'SELL': return 'text-error bg-error/10 border-error/20';
      case 'HOLD': return 'text-warning bg-warning/10 border-warning/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-success';
    if (confidence >= 0.7) return 'text-warning';
    return 'text-error';
  };

  const filteredDecisions = aiDecisions.filter(decision => {
    if (selectedConfidence === 'all') return true;
    if (selectedConfidence === 'high') return decision.confidence >= 0.8;
    if (selectedConfidence === 'medium') return decision.confidence >= 0.7 && decision.confidence < 0.8;
    if (selectedConfidence === 'low') return decision.confidence < 0.7;
    return true;
  });

  // 🔧 2025-12-30 FIX: Only show skeleton on INITIAL load, not on refreshes
  if (loading && isInitialLoad) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-card border border-border rounded-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">AI Decision Feed</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-warning rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        </div>
        <SkeletonList items={3} />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-lg p-6 glass-card-hover h-full"
    >
      {/* Header with AI branding */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <Icon name="Brain" size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">AI Decisions</h2>
            <p className="text-xs text-muted-foreground">ASTRA Oracle Insights</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
          <span className="text-xs text-muted-foreground">Live</span>
        </div>
      </div>

      {/* Decisions List - Enhanced styling */}
      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
        <AnimatePresence>
          {filteredDecisions.map((decision, index) => {
            const isExpanded = expandedId === decision.id;
            return (
            <motion.div
              key={decision.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
              className={`
                relative rounded-lg p-4 border-l-4 transition-all cursor-pointer
                ${decision.decision === 'BUY'
                  ? 'border-l-success bg-success/5 hover:bg-success/10'
                  : decision.decision === 'SELL'
                  ? 'border-l-error bg-error/5 hover:bg-error/10'
                  : 'border-l-warning bg-warning/5 hover:bg-warning/10'
                }
                ${isExpanded ? 'shadow-lg ring-1 ring-primary/20' : 'hover:shadow-md'}
              `}
              onClick={() => setExpandedId(isExpanded ? null : decision.id)}
            >
              {/* Top Row: Symbol + Decision + Confidence */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  {/* Symbol Badge */}
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center font-bold text-sm">
                    {decision.symbol.slice(0, 3)}
                  </div>
                  <div>
                    <div className="font-bold text-foreground">{decision.symbol}</div>
                    <div className="text-xs text-muted-foreground">{formatTime(decision.timestamp)}</div>
                  </div>
                </div>

                {/* Decision Badge + Confidence - THE MAIN ATTRACTION */}
                <div className="flex items-center space-x-3">
                  <span className={`
                    px-4 py-2 rounded-lg text-sm font-bold shadow-sm
                    ${decision.decision === 'BUY'
                      ? 'bg-success text-white'
                      : decision.decision === 'SELL'
                      ? 'bg-error text-white'
                      : 'bg-warning text-white'
                    }
                  `}>
                    {decision.decision === 'BUY' ? '▲' : decision.decision === 'SELL' ? '▼' : '●'} {decision.decision}
                  </span>

                  {/* Large Confidence Indicator */}
                  <div className={`
                    w-14 h-14 rounded-full flex flex-col items-center justify-center
                    ${decision.confidence >= 0.8
                      ? 'bg-success/20 border-2 border-success'
                      : decision.confidence >= 0.7
                      ? 'bg-warning/20 border-2 border-warning'
                      : 'bg-error/20 border-2 border-error'
                    }
                  `}>
                    <span className={`text-lg font-bold ${getConfidenceColor(decision.confidence)}`}>
                      {(decision.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Reasoning Summary */}
              <div className="mb-3 p-2 bg-muted/50 rounded-lg">
                <p className="text-sm text-foreground leading-relaxed">
                  {decision.reasoning}
                </p>
              </div>

              {/* Bottom Row: Factors + Status + Expand indicator */}
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {decision.factors.slice(0, 3).map((factor, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">
                      {factor}
                    </span>
                  ))}
                </div>

                <div className="flex items-center space-x-2">
                  <div className={`
                    flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
                    ${decision.executed
                      ? 'bg-success/20 text-success'
                      : 'bg-warning/20 text-warning'
                    }
                  `}>
                    <Icon name={decision.executed ? 'CheckCircle' : 'Clock'} size={12} />
                    <span>{decision.executed ? 'Executed' : 'Pending'}</span>
                  </div>
                  {/* Expand indicator */}
                  <Icon
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                    size={16}
                    className="text-muted-foreground"
                  />
                </div>
              </div>

              {/* EXPANDABLE: Full Agent Reasoning Chain */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-4 pt-4 border-t border-border"
                    onClick={(e) => e.stopPropagation()} // Prevent collapse when clicking inside
                  >
                    {/* Agent Reasoning Chain */}
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center">
                        <Icon name="GitBranch" size={12} className="mr-1" />
                        AI Agent Reasoning Chain
                      </h4>
                      <div className="space-y-2">
                        {decision.agentOutputs?.map((agent, idx) => (
                          <div
                            key={idx}
                            className="flex items-start space-x-2 p-2 bg-muted/30 rounded-lg"
                          >
                            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-primary">{idx + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-semibold text-foreground">{agent.name}</span>
                              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                {agent.output}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Risk Metrics */}
                    {decision.riskMetrics && (
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center">
                          <Icon name="Shield" size={12} className="mr-1" />
                          Risk Parameters
                        </h4>
                        <div className="grid grid-cols-4 gap-2">
                          <div className="bg-muted/30 rounded p-2 text-center">
                            <div className="text-xs text-muted-foreground">Position</div>
                            <div className="text-sm font-bold text-foreground">{decision.riskMetrics.positionSize}</div>
                          </div>
                          <div className="bg-error/10 rounded p-2 text-center">
                            <div className="text-xs text-muted-foreground">Stop Loss</div>
                            <div className="text-sm font-bold text-error">{decision.riskMetrics.stopLoss}</div>
                          </div>
                          <div className="bg-success/10 rounded p-2 text-center">
                            <div className="text-xs text-muted-foreground">Take Profit</div>
                            <div className="text-sm font-bold text-success">{decision.riskMetrics.takeProfit}</div>
                          </div>
                          <div className="bg-primary/10 rounded p-2 text-center">
                            <div className="text-xs text-muted-foreground">R:R Ratio</div>
                            <div className="text-sm font-bold text-primary">{decision.riskMetrics.riskRewardRatio}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            );
          })}
        </AnimatePresence>

        {filteredDecisions.length === 0 && (
          <div className="text-center py-12 bg-muted/30 rounded-lg">
            <Icon name="Brain" size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">No AI decisions yet</p>
            <p className="text-xs text-muted-foreground mt-1">Waiting for market signals...</p>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-sm">
          <span className="text-muted-foreground">
            <span className="font-bold text-foreground">{filteredDecisions.length}</span> decisions
          </span>
          <span className="text-success text-xs">
            {filteredDecisions.filter(d => d.executed).length} executed
          </span>
        </div>
        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Zap" size={12} className="text-primary" />
          <span>AI-Powered</span>
        </div>
      </div>
    </motion.div>
  );
};

export default AIDecisionFeed;